import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch, Prop, Ref } from 'vue-property-decorator';
import Moment from 'moment';
import { CLASSES_STORE } from '@/classes/store/classes.store';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import EdEditCourseClassComponent from '../ed-edit-course-class/ed-edit-course-class.component.vue';
import EdShareCourseComponent from '../ed-share-course/ed-share-course.component.vue';
import EdSelectTemplateComponent from './ed-select-template/ed-select-template.component.vue';
import EdCreateQuickSubjectComponent from './ed-create-quick-subject/ed-create-quick-subject.component.vue';
import { PRIVATE_ROUTES } from '@/app/private/private.routes';
import { CONTENT_TYPE } from '@/classes/values/classes.values';
import { APP_EVENT_BUS } from '@/core/enums/app.enum';
import EdInputModel from '@/models/ed-input.model';

import { FILESCACHE_KEYS } from '@/shared/plugins/files-cache/enums/files-cache.enum';
import { BUILDER_STATUS } from '@/courses/values/courses.values';
import { showCustomTooltip } from '@/utils/wc-data-table-utils';
import { PRODUCT_TYPES } from '@/course-templates/values/course-templates.values';

@Component({
  name: 'ed-list-class-courses',
  components: {
    EdEditCourseClassComponent,
    EdSelectTemplate: EdSelectTemplateComponent,
    EdCreateQuickSubject: EdCreateQuickSubjectComponent
  }
})
export default class EdListClassCoursesComponent extends Vue {

  /* Props */
  @Prop() readonly classItem: any;
  @Prop() readonly orgId: number;

  /* Refs */
  @Ref('dataTable') readonly dataTable: any;
  
  /* Data */
  public search = EdInputModel('search',  '');
  public loading: boolean = true;
  public loadingAdd: boolean = false;
  public addObj: Object = {};
  public courses: any = [];
  public coursesOrg: any = [];
  public addCourse: boolean = false;
  public selectedTemplateId: number = null;
  public quickSubjectId: number = null;
  public pagination: object = {
    page: 1,
    itemsPerPage: 10
  }
  public paginationOrg: object = {
    current: 1,
    searchTotalPages: 0,
    total: 0
  }
  public paginatorRefresh: boolean = false;
  
  readonly ITEM_TYPE_CS: string = 'CS';
  readonly ITEM_TYPE_COURSE: string = 'COURSE';
  readonly ITEM_TYPE_CSQ: string = 'CSQ';
  
  /* Computed */
  get headers() { 
    return [
      { text: this.$t('classes.headers.courseName'), value: 'title', show: true },
      { text: this.$t('classes.headers.owner'), value: 'createdBy', show: true },
      { text: this.$t('classes.headers.assignedTo'), value: 'assignedTo', show: !this.classItem.allCoursesAccess },
      { text: this.$t('classes.headers.type'), value: 'type', show: !this.addCourse },
      { text: this.$t('classes.headers.actions'), value: 'actions', sortable: false, show: true }
    ].filter(el => el.show)
  }

  get classCourses() {
    return this.$store.state.ClassesStore.classCourses;
  }

  get classTeachers() {
    return this.$store.state.ClassesStore.classTeachers;
  }

  get pagedOrganizationCourses() {
    return this.$store.state.ClassesStore.pagedOrganizationCourses;
  }

  get writeClassPriv() {
    return this.$store.state.AuthStore.privileges.WriteClass;
  }

  get isMainTeacher() {
    return this.userId === this.classItem.teacher.id;
  }

  get userId() {
    return this.$store.state.AuthStore.user.id;
  }

  get isCalendarExpired() {
    return Moment(this.classItem.schoolDay?.endDate).isBefore(Moment());
  }

  get quickSubjectButtonTitle() {
    return this.isCalendarExpired ? this.$t('classes.courses.calendarExpired') : '';
  }

  get itemsPerPage(): number {
    return this.pagination['itemsPerPage'];
  }

  get paginationPage(): number {
    return this.addCourse ? this.paginationOrg['current'] : this.pagination['page'];
  }

  get paginationPageIndex(): number {
    return this.paginationPage - 1;
  }

  get paginationLength(): number {

    if (this.addCourse) {
      return this.search.value === '' ?
        this.paginationOrg['total'] ?? 0
        : this.paginationOrg['searchTotalPages'] ?? 0;
    }

    const totalItems: number = this.classCourses?.length ?? 0;
    return (totalItems % this.itemsPerPage === 0)
      ? totalItems / this.itemsPerPage
      : Math.floor((totalItems / this.itemsPerPage) + 1);
  }

  get pagedItems(): any[] {

    if (this.addCourse) {
      return this.coursesOrg;
    }

    const start: number = (this.paginationPageIndex) * this.itemsPerPage;
    const end: number = start + this.itemsPerPage;

    return this.classCourses?.slice(start, end);
  }

  /* Methods */
  isCourseOwner(id) {
    return id === this.userId
  }

  shareItem(course) {
    this.getClassCourses().then((course) => {
      this.$EdModal.createModal(EdShareCourseComponent, { course } );
    });
  }

  getFirstContentItem(data) {
    return Array.isArray(data) && data.length > 0
      ? data[0]
      : null;
  }

  deleteItem (item: any) {
    if(item.type === this.ITEM_TYPE_CSQ) {
      this.$EdModal.createConfirmationModal(
        {
          body: this.$t('classes.courses.confirmRemoveQuickSubject', {courseName: item.title, className: this.classItem.name}), 
          action: async () => {
            await this.deleteQuickItemConfirm(item);
          }
        }
      );
    } else {
      this.$EdModal.createConfirmationModal(
        {
          body: this.$t('classes.courses.confirmRemove', {courseName: item.title, className: this.classItem.name}), 
          action: async () => {
            await this.deleteItemConfirm(item);
          }
        }
      );
    }
  }

  deleteItemConfirm (item) {
    this.$store.dispatch(`${CLASSES_STORE.ACTIONS.REMOVE_COURSE_FROM_CLASS_A}`, 
    { classId: this.classItem.id,
      courseId: item['id']}
    ).then(() => {
      this.dataTable?.updateItem(item);
      this.$EdToast.create({ 
        msg:  `${this.$t('classes.success.courseRemoved')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
    })
  }

  async deleteQuickItemConfirm (item) {
    try {
      await this.$store.dispatch(CLASSES_STORE.ACTIONS.REMOVE_QUICK_CONTENT_A, {quickContentId: item.id, classId: this.classItem.id});
      if(item.builderId) {
        await this.$Builder.Services.deleteAllDataFromIndexDB(item.builderId);
      }
      this.$EdToast.create({
        msg:  `${this.$t('classes.success.courseRemoved')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
    } catch {
      this.$EdToast.create({ 
        msg:  `${this.$t('classes.errors.courseNoRemoved')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    }
  }

  editCourseClassModal(course) {
    this.$EdModal.createModal(EdEditCourseClassComponent, {course, classItem: this.classItem, orgId: this.orgId});
  }

  addCourseToClass(course: Object) {
    // Add course if it's no added previously
    if(this.classCourses.findIndex(el => el.id === course['id']) === -1) {
      this.loadingAdd = true;
      this.addObj = course;
      this.dataTable?.updateItem(course);
      this.$store.dispatch(`${CLASSES_STORE.ACTIONS.ADD_COURSE_TO_CLASS_A}`, 
        { classId: this.classItem.id,
        courseId: course['id']}
      ).then(() => {
        this.loadingAdd = false;
        this.addObj = {};
        this.dataTable?.updateItem(course);
        this.$EdToast.create({ 
          msg:  `${this.$t('classes.success.courseAdded')}`,
          type: TOAST_TYPES.SUCCESS,
          top: '3rem',
          timeout: 3000
        });
        this.editCourseClassModal(course)
      })
    }
  }

  async getClassCourses() {
    this.loading = true;
    
    let builderContent = await this._getBuilderDataFromStore();
    // Get All Class Courses
   return this.$store.dispatch(`${CLASSES_STORE.ACTIONS.GET_CLASS_COURSES_A}`,{
      classId: this.classItem.id,
      builderContent: builderContent
    }).then((val) => {
      this.loading = false;
      return this.getFirstContentItem(val);
    }).catch(() => {
      // handle error
      this.loading = false;
    })
  }

  async _getBuilderDataFromStore() {
    let builderContent = Array<any>();
    const data = await Vue.prototype.$BuilderCache.getDataByPrefix('builder_', FILESCACHE_KEYS.BUILDER_DATA);
    if (data) {
      data
        .filter(item => item.subject.type === CONTENT_TYPE.QUICK_SUBJECT && item.subject.classId === this.classItem.id)
        .sort((a, b) => b.builderId - a.builderId)
        .forEach(item => {
          const builderId = item.builderId;
          const id = item.subject.id;
          item.subject.fromId = item.builderId;
          item.subject.id = (BUILDER_STATUS.DONE === item.status) ? item.contentId : builderId;
          builderContent.push({
            builderId: builderId,
            courseTeachers: [],
            title: item.subject.title,
            id: item.subject.id,
            originTool: 'BUILDER',
            owner: {},
            status: item.status,
            syncStatus: item.syncStatus,
            contentId: item.contentId,
            version: item.version,
            type: item.subject.type,
            hasSchedule: true
          });
        });
    }

    return builderContent;
  }

  getOrganizationCourses() {
    if(this.isCalendarExpired)
      return;
    
    this.loading = true;
    this.addCourse = true;
    this.$store.dispatch(`${CLASSES_STORE.ACTIONS.GET_ORGANIZATION_COURSES_A}`,{
      orgId: this.orgId, page: this.paginationOrg['current']-1, pageSize: this.itemsPerPage, orderByClassId: this.classItem.id
    }).then((res) => { 
      this.coursesOrg = res.content;
      this.paginationOrg['total'] = res.totalPages;
      this.loading = false;
    }).catch(() => {
      // Handle error
      this.coursesOrg = [];
      this.addCourse = false;
      this.loading = false;
    });
  }

  onPageChange(customEvent: CustomEvent): void {
    const detail: { page: number } = customEvent.detail;
    if (this.addCourse) {
      this.paginationOrg['current'] = detail.page;

      if (this.search.value === '') {
        this.getOrganizationCourses();

      } else {
        this.searchStudents(true);
      }

    } else {
      this.pagination['page'] = detail.page;
    }
  }

  getAssignedToList(assigned) {
    return assigned.map(el => el.firstName).join(', ')
  }

  createQuickSubject(templateId) {
    if(templateId) {
      this.selectedTemplateId = templateId;
      this.onOpenSubjectModal();
    }
  }

  onStartSubjectBuilder(item) {
    this.$router.push({
      name: PRIVATE_ROUTES.SUBJECT_BUILDER.NAME,
      params: {
        id: item.id,
      },
      query: {
        type: CONTENT_TYPE.QUICK_SUBJECT,
        classId: this.classItem.id
      }
    });
  }

  onStartSubjectBuilderV2(item) {
    const query: any = {
      type: CONTENT_TYPE.QUICK_SUBJECT,
      classId: this.classItem.id,
    };

    if (item.builderId) {
      query.builderId = item.builderId;
      query.syncStatus = item.syncStatus ? item.syncStatus : null
    }

    this.$router.push({
      name: PRIVATE_ROUTES.SUBJECT_BUILDER_V2.NAME,
      params: {
        id: item.id,
      },
      query: query
    });
  }

  getTypeFormatted(type) {
    switch(type) {
      case this.ITEM_TYPE_CS:
      case this.ITEM_TYPE_COURSE:
        return this.$t('classes.courses.subject');
      case this.ITEM_TYPE_CSQ:
        return this.$t('classes.courses.quickAddSubject');
    }
  } 

  hasUpdateQuickSubjectPermission(item) {
    if(this.classItem.allCoursesAccess) {
      return this.isCourseOwner(item.owner.id) || this.classTeachers.find(el => el.person.id === this.userId)
    } else {
      return this.isCourseOwner(item.owner.id) 
        || this.classCourses.find(el=> el.id === item.id)?.courseTeachers?.find(el => el.id === this.userId)
    }
  }

  isDraftQuickSubject(item) {
    if(item.builderId) {
      return true;
    }
    return false;
  }

  getClassTeachers() {
    this.loading = true;
    
    // Get All Class Teachers
    this.$store.dispatch(`${CLASSES_STORE.ACTIONS.GET_CLASS_TEACHERS_A}`,{
      classId: this.classItem.id
    }).then(() => {
      this.loading = false;
    }).catch(() => {
      this.loading = false;
    })
  }

  public refreshDataTable(): void {
    this._loadDataTable();
  }

  private _loadDataTable(): void {
    if (!this.dataTable || !this.dataTable.loadData) {
      return;
    }

    if (!this.pagedItems || this.pagedItems?.length == 0) {
      this.dataTable.loadData({ items: [], headers: this.headers });
      return;
    }

    const slots = {
      'item.title': {
        template: document.querySelector('#item\\.title').innerHTML,
        templateParams: [{ key: 'item.title' }],
        customTooltip: [{
            key: 'item.title',
            show: showCustomTooltip(this, (params: any) => params?.item?.title),
        }],
      },
      'item.createdBy': {
        template: document.querySelector('#item\\.createdBy').innerHTML,
        templateParams: [{
          key: 'item.createdBy',
          value: (params) => params.item.owner?.fullName ?? '',
        }],
      },
      'item.assignedTo': {
        template: document.querySelector('#item\\.assignedTo').innerHTML,
        templateParams: [
          {
            key: 'item.assignedTo',
            value: (params) => {
              const course = params.parent.classCourses.find(el => el.id === params.item.id);
              return course?.courseTeachers?.length > 0 ? `${course.courseTeachers[0].fullName}...` : '';
            }
          },
        ],
        customTooltip: [
          {
            key: 'item.assignedToTooltip',
            show: showCustomTooltip(this, (params: any) => {
              const course = params.parent.classCourses.find(el => el.id === params.item.id);
              return course?.courseTeachers?.length > 0
                ? params.parent.getAssignedToList(course.courseTeachers) : '';
            }),
          },
        ],
      },
      'item.type': {
        template: document.querySelector('#item\\.type').innerHTML,
        templateParams: [{
          key: 'item.type',
          value: (params) => params.parent.getTypeFormatted(params.item.type) ?? '',
        }],
      },
      'item.actions': {
        template: document.querySelector('#item\\.actions').innerHTML,
        templateParams: [
          {
            key: 'editSubjectPermission',
            value: (params) => params.parent.hasUpdateQuickSubjectPermission(params.item)
              ? params.parent.$t('common.edit')
              : params.parent.$t('classes.info.noPermission'),
          },
          {
            key: 'deleteSubjectPermission',
            value: (params) => (params.item.type !== params.parent.ITEM_TYPE_CSQ || params.parent.hasUpdateQuickSubjectPermission(params.item) || params.parent.isDraftQuickSubject(params.item))
              ? params.parent.$t('classes.courses.removeCourse')
              : params.parent.$t('classes.info.noPermission'),
          },
          {
            key: 'subjectBuilderPermission',
            value: (params) => params.parent.hasUpdateQuickSubjectPermission(params.item) && params.item.hasSchedule 
              ? params.parent.$t('classes.courses.subjectBuilder')
              : params.item.hasSchedule 
                ? params.parent.$t('classes.info.noPermission')
                : params.parent.$t('classes.info.needToHaveSchedule'),
          },
          {
            key: 'subjectBuilderPermissionV2',
            value: (params) => (params.parent.hasUpdateQuickSubjectPermission(params.item) && params.item.hasSchedule) || params.parent.isDraftQuickSubject(params.item)
              ? params.parent.$t('classes.courses.subjectBuilder')
              : params.item.hasSchedule
                ? params.parent.$t('classes.info.noPermission')
                : params.parent.$t('classes.info.needToHaveSchedule'),
          },
        ],
        expressions: [
          {
            target: '.spinner', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.loadingAdd && (params.parent.addObj.id === params.item.id),
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.actionButtons', attribute: 'style', booleanAttribute: false,
            expression: (params) => !params.parent.loadingAdd,
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.addedSubject', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.classCourses.find(el => el.id === params.item.id),
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.setupSubject', attribute: 'style', booleanAttribute: false,
            expression: (params) => {
              return ((params.item.type !== params.parent.ITEM_TYPE_CSQ)
                && (params.parent.classCourses.find(el => el.id === params.item.id)
                  && (params.parent.writeClassPriv || params.parent.isMainTeacher))
              );
            },
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.setupSubject', attribute: 'class', booleanAttribute: false, concat: true,
            expression: (params) =>  params.parent.isCalendarExpired,
            applied: 'iconDisabled', fallback: ' '
          },
          {
            target: '.addSubject', attribute: 'style', booleanAttribute: false,
            expression: (params) => {
              return params.parent.addCourse
                && params.parent.classCourses.findIndex(el => el.id === params.item.id) === -1
                && (params.parent.addObj.id !== params.item.id);
            },
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.editSubject', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.type === params.parent.ITEM_TYPE_CSQ,
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.editSubject', attribute: 'disabled', booleanAttribute: true,
            expression: (params) => !params.parent.hasUpdateQuickSubjectPermission(params.item),
            applied: 'true', fallback: 'false'
          },
          {
            target: '.editSubject', attribute: 'class', booleanAttribute: false, concat: true,
            expression: (params) =>  !params.parent.hasUpdateQuickSubjectPermission(params.item) || 
                                      (params.item.hasLocalBuilder || params.item.version) ||
                                      params.parent.isCalendarExpired,
            applied: 'iconDisabled', fallback: ' '
          },
          {
            target: '.deleteSubject', attribute: 'style', booleanAttribute: false,
            expression: (params) => {
              return ((params.parent.classCourses.find(el => el.id === params.item.id)
                  && (params.parent.writeClassPriv || params.parent.isMainTeacher))
              );
            },
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.deleteSubject', attribute: 'disabled', booleanAttribute: true,
            expression: (params) => (params.item.type === params.parent.ITEM_TYPE_CSQ && !(params.parent.hasUpdateQuickSubjectPermission(params.item) || params.parent.isDraftQuickSubject(params.item))),
            applied: 'true', fallback: 'false'
          },
          {
            target: '.deleteSubject', attribute: 'class', booleanAttribute: false, concat: true,
            expression: (params) => params.item.type === params.parent.ITEM_TYPE_CSQ && 
                                  !(params.parent.hasUpdateQuickSubjectPermission(params.item) ||  params.parent.isDraftQuickSubject(params.item)) || 
                                  params.parent.isCalendarExpired,
            applied: 'iconDisabled', fallback: ' '
          },
          {
            target: '.subjectBuilder', attribute: 'style', booleanAttribute: false,
            expression: (params) => {
              return params.parent.classCourses.find(el => el.id === params.item.id) 
                && (params.parent.writeClassPriv || params.parent.isMainTeacher)
                && params.item.type === params.parent.ITEM_TYPE_CSQ;
            },
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.subjectBuilder', attribute: 'disabled', booleanAttribute: true,
            expression: (params) => !(params.parent.hasUpdateQuickSubjectPermission(params.item) && params.item.hasSchedule),
            applied: 'true', fallback: 'false'
          },
          {
            target: '.subjectBuilder', attribute: 'class', booleanAttribute: false, concat: true,
            expression: (params) => !(params.parent.hasUpdateQuickSubjectPermission(params.item) && params.item.hasSchedule),
            applied: 'iconDisabled', fallback: ' '
          },
          {
            target: '.subjectBuilderV2', attribute: 'style', booleanAttribute: false,
            expression: (params) => {
              return params.parent.classCourses.find(el => el.id === params.item.id)
                && (params.parent.writeClassPriv || params.parent.isMainTeacher)
                && params.item.type === params.parent.ITEM_TYPE_CSQ || (params.item.status === BUILDER_STATUS.DONE && params.item.originTool === PRODUCT_TYPES.BUILDER);
            },
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.subjectBuilderV2', attribute: 'disabled', booleanAttribute: true,
            expression: (params) => !(params.parent.hasUpdateQuickSubjectPermission(params.item) && params.item.hasSchedule || params.parent.isDraftQuickSubject(params.item)) ,
            applied: 'true', fallback: 'false'
          },
          {
            target: '.subjectBuilderV2', attribute: 'class', booleanAttribute: false, concat: true,
            expression: (params) => !(params.parent.hasUpdateQuickSubjectPermission(params.item) && params.item.hasSchedule || params.parent.isDraftQuickSubject(params.item))
                                    || params.parent.isCalendarExpired,
            applied: 'iconDisabled', fallback: ' '
          },
          {
            target: '.subjectBuilderV2', attribute: 'class', booleanAttribute: false, concat: true,
            expression: (params) => params.item.status === BUILDER_STATUS.CONFLICT,
            applied: 'subjectBuilderConflict', fallback: ' '
          },
          {
            target: '.shareCourse', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.addObj.id !== params.item.id && params.item.type !== params.parent.ITEM_TYPE_CSQ,
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.shareCourse', attribute: 'class', booleanAttribute: false, concat: true,
            expression: (params) =>  params.parent.isCalendarExpired,
            applied: 'iconDisabled', fallback: ' '
          },
        ],
        events: [
          { target: '.setupSubject', handler: (params) => params.parent.editCourseClassModal(params.item) },
          { target: '.addSubject', handler: (params) => params.parent.addCourseToClass(params.item) },
          { target: '.deleteSubject', handler: (params) => params.parent.deleteItem(params.item) },
          { target: '.subjectBuilder', handler: (params) => params.parent.onStartSubjectBuilder(params.item) },
          { target: '.subjectBuilderV2', handler: (params) => params.parent.onStartSubjectBuilderV2(params.item) },
          { target: '.shareCourse', handler: (params) => params.parent.shareItem(params.item) },
          {
            target: '.editSubject',
            handler: (params) => {
              params.parent.quickSubjectId = params.item.id;
              params.parent.onOpenSubjectModal()
            }
          },
        ],
      },
      parent: this,
    };

    this.dataTable.loadData({
      items: this.pagedItems,
      headers: this.headers,
      slots: slots,
    });
  }

  private _onLanguageChanged(lang: string): void {
    this.$nextTick(() => {
      this.dataTable?.setAttribute('lang', lang);
      this._loadDataTable();
    });
  }

  onOpenTemplateModal () {
    this.$EdModal.createModal(EdSelectTemplateComponent, { 
      selectTemplate: this.createQuickSubject
    });
  }

  onOpenSubjectModal () {
    this.$EdModal.createModal(EdCreateQuickSubjectComponent, { 
      resetModal: () => { this.quickSubjectId = null },
      templateId: this.selectedTemplateId,
      classId: this.classItem.id,
      quickSubjectId: this.quickSubjectId
    });
  }

  mounted() {
    this.$edEventBus.$on(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
    if(this.classItem.allCoursesAccess) {
      this.getClassTeachers();
    }
  }

  beforeDestroy() {
    this.$edEventBus.$off(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
  }

  /* Watch */
  @Watch('classItem', { immediate: true })
  getCoursesByClass() {
    this.addCourse = false;
    this.search.value = '';
    this.$store.commit(CLASSES_STORE.MUTATIONS.CLEAR_COURSES_M);
    this.getClassCourses();
  }


  @Watch('search.value', { immediate: true, deep: true })
  private _watchSearch(value): void {
    this.searchStudents();
  }

  searchStudents(changedPage: boolean = false) {
    if (!changedPage) {
      this.paginationOrg['current'] = 1;
      this.paginationOrg['searchTotalPages'] = 0;
    }
    this.loading = true;
    this.$store.dispatch(`${CLASSES_STORE.ACTIONS.SEARCH_COURSES_ORGANIZATION_A}`,{
      orgId: this.orgId,
      page: this.paginationPageIndex,
      pageSize: this.itemsPerPage,
      keyword: this.search.value,
      orderByClassId: this.classItem.id
    }).then((res) => {
      this.paginationOrg['searchTotalPages'] = res.totalPages ?? 0,
      this.coursesOrg = res.content
    }).catch(() => {
      this.coursesOrg = []
    }).finally(() => {
      this.loading = false;
    });
  }

  @Watch('classCourses', { deep: true, immediate: true })
  private _watchClassCourses(): void {
    // Needed because of last added item goes first
    this.pagination['page'] = 1;
  }

  @Watch('pagedItems', { deep: true, immediate: true })
  private _watchPagedItems(): void {
    this._scheduleLoadDataTable();
  }

  @Watch('paginationPage', { deep: true, immediate: true })
  private _watchPaginationPage(): void {
    this._scheduleLoadDataTable();
  }

  @Watch('paginationLength', { deep: true, immediate: true })
  private _watchPaginationLength(): void {
    // Refresh the pagination component from initial state
    if (this.paginationLength === 0) {
      this.paginatorRefresh = true;
      this.$nextTick(() => {
        this.paginatorRefresh = false;
      });
    }
  }

  private _scheduleLoadDataTable(): void {
    this.$nextTick(() => {
      this._loadDataTable();
    });
  }
}
